import { z } from 'zod'
import {
  ChangeId,
  EndDate,
  Id,
  IsActive,
  IsActual,
  NextId,
  ObjectId,
  ObjectUuid,
  OperationTypeId,
  PrevId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

export namespace Room {
  export interface Data {
    /** @description Unique record identifier. Key field */
    readonly id: number

    /** @description Global unique identifier of the address object */
    readonly objectId: number

    /** @description Global unique identifier of the object (UUID) */
    readonly objectGuid: string

    /** @description Identifier of the changing transaction */
    readonly changeId: number

    /** @description Room or office number */
    readonly number: string

    /** @description Room or office type */
    readonly roomType: number

    /** @description Record action status */
    readonly operationTypeId: number

    /** @description Identifier of the record linking to the previous historical record */
    readonly prevId?: number

    /** @description Identifier of the record linking to the subsequent historical record */
    readonly nextId?: number

    /** @description Record update date */
    readonly updateDate: Date

    /** @description Record start date */
    readonly startDate: Date

    /** @description Record end date */
    readonly endDate: Date

    /** @description Address object relevance indicator */
    readonly isActual: boolean

    /** @description Active address object indicator */
    readonly isActive: boolean
  }
}

/** @description Room information */
export class Room extends GarItem<'Room', Room.Data> {
  static readonly SCHEMA: GarItem.Schema<Room.Data> = z
    .strictObject({
      ID: Id,

      OBJECTID: ObjectId,

      OBJECTGUID: ObjectUuid,

      CHANGEID: ChangeId,

      NUMBER: z.string().describe('Room or office number'),

      ROOMTYPE: z.coerce.number<string>().int().describe('Room or office type'),

      OPERTYPEID: OperationTypeId,

      PREVID: PrevId.optional(),

      NEXTID: NextId.optional(),

      UPDATEDATE: UpdateDate,

      STARTDATE: StartDate,

      ENDDATE: EndDate,

      ISACTUAL: IsActual,

      ISACTIVE: IsActive,
    })
    .transform<Room.Data>(attributes => ({
      id: attributes.ID,
      objectId: attributes.OBJECTID,
      objectGuid: attributes.OBJECTGUID,
      changeId: attributes.CHANGEID,
      number: attributes.NUMBER,
      roomType: attributes.ROOMTYPE,
      operationTypeId: attributes.OPERTYPEID,
      prevId: attributes.PREVID,
      nextId: attributes.NEXTID,
      updateDate: attributes.UPDATEDATE,
      startDate: attributes.STARTDATE,
      endDate: attributes.ENDDATE,
      isActual: attributes.ISACTUAL,
      isActive: attributes.ISACTIVE,
    }))

  readonly type = 'Room'

  protected get schema() {
    return Room.SCHEMA
  }
}
